package com.arena_web.ui.sections.news

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.LocalSportColors
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier

/** The News page's content modes, switched from the top bar. */
enum class NewsPageMode { News, Matches }

/**
 * Two-icon mode switcher that lives in the top bar (the News page swaps it in
 * for the notification bell on phones and mounts it in the desktop header's
 * action group). House = news home, trophy cup = leagues & tournaments —
 * football24-style line icons.
 */
@Composable
fun NewsModeToggle(mode: NewsPageMode, onModeChange: (NewsPageMode) -> Unit, mobile: Boolean) {
    val outerH = if (mobile) 36 else 38
    // Capsule-shaped inner buttons — noticeably wider than tall so the
    // switcher reads as a segmented control, not two round buttons.
    val innerW = if (mobile) 44 else 48
    val innerH = if (mobile) 30 else 32
    val iconSize = if (mobile) 18 else 19
    Row(
        modifier = Modifier
            .styleModifier {
                property("height", "${outerH}px")
                property("border-radius", "100px")
                property("padding", "3px")
                property("gap", "3px")
                property("align-items", "center")
                property("flex-shrink", "0")
                // Same chrome as the round top-bar buttons.
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        val newsActive = mode == NewsPageMode.News
        // Single variant — the active state only recolours it white (via
        // the on-primary filter below).
        ToggleButton(
            AppIcons.Newspaper,
            active = newsActive, w = innerW, h = innerH, iconSize = iconSize,
        ) { onModeChange(NewsPageMode.News) }
        ToggleButton(
            if (!newsActive) AppIcons.NewsMatchesFilled else AppIcons.NewsMatches,
            active = !newsActive, w = innerW, h = innerH, iconSize = iconSize,
        ) { onModeChange(NewsPageMode.Matches) }
    }
}

@Composable
private fun ToggleButton(icon: String, active: Boolean, w: Int, h: Int, iconSize: Int, onClick: () -> Unit) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("width", "${w}px")
                property("height", "${h}px")
                property("border-radius", "100px")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("transition", "background 0.15s ease")
                if (active) property("background", sportColors.primary.toString())
            },
        contentAlignment = Alignment.Center
    ) {
        Icon(
            src = icon,
            modifier = Modifier.styleModifier {
                property("width", "${iconSize}px")
                property("height", "${iconSize}px")
                property(
                    "filter",
                    if (active) sportColors.iconFilterOnPrimary else com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V,
                )
            }
        )
    }
}
